import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, ShieldCheck, Bot, ScanSearch, Ban, EyeOff, Cpu, FileCheck2,
  Users, Fingerprint, Wrench, Play, Loader2, BookOpen, Boxes, Server, Award, ScrollText, KeyRound, Lock, Check,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import { Button } from "../components/ui/button";
import AdversarialDemo from "../components/defence/AdversarialDemo";
import DeploymentExplorer from "../components/defence/DeploymentExplorer";
import { track, EVENTS } from "../lib/analytics";

const USE_CASES = [
  { id: "employee", icon: Users, name: "Employee generative AI", desc: "Govern how staff use ChatGPT, Claude, Copilot and other assistants — controlling what goes in and what comes back.", points: ["Block sensitive uploads", "Warn on risky prompts", "Inspect responses", "Evidence every intervention"] },
  { id: "copilots", icon: Bot, name: "Enterprise copilots", desc: "Apply your policy to embedded copilots across your day-to-day tools and suites.", points: ["Role-based policy", "Output inspection", "Escalate material cases", "Consistent across vendors"] },
  { id: "agents", icon: Cpu, name: "AI agents & tool actions", desc: "Evaluate proposed agent actions against an allow-list and hold high-impact actions for authorisation.", points: ["Action allow-list", "Hold for approval", "Scope tool access", "Record approve / reject"] },
  { id: "knowledge", icon: BookOpen, name: "Knowledge assistants", desc: "Apply access and handling policies around assistants that read internal knowledge.", points: ["Enforce source access", "Constrain what is surfaced", "Handle sensitive content", "Log retrievals"] },
  { id: "shadow", icon: EyeOff, name: "Shadow & unmanaged AI", desc: "Bring visibility and control to AI that appears outside sanctioned tooling.", points: ["Surface unsanctioned use", "Apply baseline policy", "Warn or block", "Report to owners"] },
  { id: "supplier", icon: Boxes, name: "Third-party & supplier AI", desc: "An independent control layer around approved external AI, not reliant solely on provider safeguards.", points: ["Independent of provider", "Consistent policy", "Inspect in/out", "Evidence for assurance"] },
];

function UseCaseTabs() {
  const [active, setActive] = useState(USE_CASES[0].id);
  const cur = USE_CASES.find((c) => c.id === active);
  const Icon = cur.icon;
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4" data-testid="enterprise-usecases">
      <div className="flex flex-col gap-1.5">
        {USE_CASES.map((c) => {
          const CIcon = c.icon; const on = c.id === active;
          return (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`enterprise-usecase-${c.id}`}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${on ? "border-primary/40 bg-primary/[0.07]" : "border-white/10 bg-navy-900/60 hover:border-white/20"}`}>
              <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-muted-foreground"}`}><CIcon className="h-5 w-5" /></span>
              <span className={`text-sm font-medium ${on ? "text-foreground" : "text-muted-foreground"}`}>{c.name}</span>
            </button>
          );
        })}
      </div>
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
          <h3 className="text-xl font-bold text-foreground">{cur.name}</h3>
        </div>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{cur.desc}</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
          {cur.points.map((p) => (
            <div key={p} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span className="text-sm text-foreground/90">{p}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

const FLOW = [
  { icon: Bot, step: "AI use happens", desc: "An employee uses a copilot, or an agent attempts a tool action." },
  { icon: ScanSearch, step: "Inspect", desc: "norse3 inspects the input, the output and the intended action." },
  { icon: ShieldCheck, step: "Policy", desc: "Your organisation-defined policy is applied in real time." },
  { icon: Ban, step: "Intervene", desc: "Allow, warn, redact, block or escalate before anything is released or executed." },
  { icon: FileCheck2, step: "Evidence", desc: "A reviewable record is retained for oversight." },
];

const CONTROLS = [
  { icon: Fingerprint, t: "Stop sensitive uploads", d: "Prevent employees entering passports, IDs and confidential documents into AI tools." },
  { icon: EyeOff, t: "Inspect AI outputs", d: "Check responses before they reach a person or a downstream system." },
  { icon: Wrench, t: "Control agent actions", d: "Constrain what copilots and agents may do with an action allow-list." },
  { icon: ShieldCheck, t: "Organisation-defined policy", d: "Apply your own rules by role, system and sensitivity." },
  { icon: Cpu, t: "Across your AI estate", d: "Copilots, enterprise assistants and multi-vendor models." },
  { icon: Users, t: "Human escalation", d: "Route material cases to named reviewers who decide." },
];

const FAQ = [
  ["Does it replace our AI model?", "No. norse3 is an independent control layer that sits alongside your models and systems. The model stays separate."],
  ["Can it stop an agent taking an action?", "Where configured, norse3 can inspect and block an out-of-policy action before it executes, and escalate to an owner."],
  ["How is it deployed?", "Hosted, private cloud or customer-controlled environments, chosen against your sensitivity and residency requirements."],
  ["How is this different from native guardrails?", "Native guardrails optimise for the model's own safety. norse3 applies your firm-specific policy, across a multi-vendor estate, with reviewable evidence."],
];

function EnterpriseMock() {
  const [running, setRunning] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const run = () => { setRunning(true); setBlocked(false); track(EVENTS.SCENARIO_STARTED, { scenario: "enterprise_agent" }); setTimeout(() => { setRunning(false); setBlocked(true); track(EVENTS.SCENARIO_COMPLETED, { scenario: "enterprise_agent" }); }, 1200); };
  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/80 shadow-2xl shadow-black/40 p-5" data-testid="enterprise-mock">
      <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-primary"><Bot className="h-4 w-4" /></span>
        <p className="text-sm font-semibold text-foreground">Copilot agent</p>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">Agent requested: <span className="text-foreground font-mono text-xs">delete_customer_records()</span></p>
      {!blocked ? (
        <Button size="sm" onClick={run} disabled={running} className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="enterprise-run">
          {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Run action
        </Button>
      ) : (
        <div className="mt-4 space-y-2">
          <div className="rounded-lg border border-signal-red/40 bg-signal-red/[0.07] px-3 py-2 text-sm text-signal-red flex items-center gap-2"><Ban className="h-4 w-4" /> Action BLOCKED — not on allow-list</div>
          <div className="rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2 text-xs text-muted-foreground">Policy applied · owner notified · evidence recorded</div>
        </div>
      )}
    </div>
  );
}

export default function EnterpriseAI() {
  useEffect(() => { document.title = "Enterprise AI | norse3"; }, []);
  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-14 lg:pt-36 lg:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div className="max-w-2xl animate-fade-up">
              <Eyebrow>Enterprise AI</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-[1.06] text-foreground text-balance">Runtime control for enterprise AI.</h1>
              <p className="mt-4 text-base font-semibold text-primary">In plain English: it keeps company AI, copilots and agents inside your rules — checking what goes in, what comes out and what they do.</p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                norse3 applies your policies to employee AI, copilots, enterprise assistants and
                agents — inspecting inputs, outputs and the actions they attempt, and intervening
                before anything is released or executed.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo" data-testid="enterprise-hero-cta">Book a 20-minute walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
                <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/skjold">See Skjold (document guard)</Link></Button>
              </div>
            </div>
            <div className="animate-fade-up lg:pl-4" style={{ animationDelay: "120ms" }}><EnterpriseMock /></div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="The problem" title="AI is now inside your workforce and your systems." subtitle="Employees paste sensitive data into chatbots, copilots draft on your behalf and agents take actions. Native model guardrails don't enforce your policy, across your estate, with evidence." /></Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="How it works" title="Inspect → policy → intervene → evidence." /></Reveal>
          <div className="mt-8 grid gap-4 lg:grid-cols-5 sm:grid-cols-2">
            {FLOW.map((f, i) => { const Icon = f.icon; return (
              <Reveal key={f.step} delay={i * 70}><div className="relative h-full rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <span className="absolute right-4 top-4 text-xs font-mono text-primary/50">0{i + 1}</span>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-base font-semibold text-foreground">{f.step}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div></Reveal>
            ); })}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Key controls" title="What Enterprise AI controls." /></Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CONTROLS.map((x, i) => { const Icon = x.icon; return (
              <Reveal key={x.t} delay={i * 50}><div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
              </div></Reveal>
            ); })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Use cases" title="One control layer across enterprise AI." /></Reveal>
          <Reveal className="mt-8" delay={80}><UseCaseTabs /></Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="Adversarial AI" title="Enterprise AI will be tested by more than ordinary users." subtitle="Run a synthetic attack and watch it move through detection, policy, intervention, incident and review. Demonstrations only." />
          </Reveal>
          <Reveal className="mt-8" delay={80}><AdversarialDemo /></Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="Deployment" title="Deploy according to your environment." subtitle="An independent control layer between your users and the approved AI services you run." />
          </Reveal>
          <Reveal className="mt-8" delay={80}><DeploymentExplorer /></Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Assurance" title="Built for organisations where evidence matters." subtitle="Showing only what is verified today." /></Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Award, t: "ISO 9001", d: "Quality management — certified.", v: true },
              { icon: Award, t: "ISO 42001", d: "AI management system — certified.", v: true },
              { icon: ScrollText, t: "SOC 2", d: "Roadmap / readiness in progress — not yet certified.", v: false },
              { icon: ShieldCheck, t: "Policy controls", d: "Configurable, versioned organisation-defined policies." },
              { icon: KeyRound, t: "Configurable access", d: "Role-based access to the console and records." },
              { icon: Lock, t: "Configurable retention", d: "Retention windows configured to your policy." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    {x.v === true && <span className="rounded-full border border-signal-green/40 bg-signal-green/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-green">VERIFIED</span>}
                    {x.v === false && <span className="rounded-full border border-signal-amber/40 bg-signal-amber/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-amber">ROADMAP</span>}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8"><Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/trust-centre">Visit Trust Centre <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button></div>
        </div>
      </section>

      <section className="border-t border-white/10 py-14 lg:py-16 bg-navy-950/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="FAQ" title="Common questions." /></Reveal>
          <div className="mt-8 space-y-3">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground flex items-center justify-between">{q}<ArrowDown className="h-4 w-4 text-primary transition-transform group-open:rotate-180" /></summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-lg border border-white/10 bg-gradient-to-b from-navy-800/60 to-navy-900/60 p-8 lg:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">Bring runtime control to your AI estate.</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
              <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground"><Link to="/governance-tools">Assess your AI governance</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
